import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ROLE_LABEL } from "@/lib/constants";
import { AssignedAreasEditor, AssignedAreasBadges } from "@/components/AssignedAreas";
import { Plus, Trash2, Pencil, Users2, MapPin } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "panitia", "ppdp", "rt", "rw"];

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [dusuns, setDusuns] = useState([]);
  const [tpsList, setTpsList] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadTps = () =>
    api.get("/admin/ppdp-mapping/simulate")
      .then(({ data }) => setTpsList(data?.rows || []))
      .catch(() => {});

  useEffect(() => {
    load();
    api.get("/dusuns").then(({ data }) => setDusuns(data || [])).catch(() => {});
    // Ambil ringkasan TPS dari endpoint pemetaan (sudah agregasi & dedup dusun ganda)
    loadTps();
  }, []);

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Pengguna diperbarui.");
      } else {
        await api.post("/users", payload);
        toast.success("Pengguna ditambahkan.");
      }
      setOpenForm(false); setEditing(null);
      load();
      // Refresh TPS list agar status "SUDAH DI-ASSIGN" ter-update setelah edit
      if (payload.role === "ppdp" || payload.assigned_tps !== undefined) {
        loadTps();
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Hapus pengguna ${u.username}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Pengguna dihapus."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Manajemen Pengguna</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Daftar Pengguna</h1>
          <p className="text-slate-600 text-sm mt-2">Kelola akun Admin, Panitia, PPDP, RT, dan RW.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpenForm(true); }} className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="add-user-button">
          <Plus className="h-4 w-4 mr-2" /> Tambah Pengguna
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((u) => (
          <div key={u.id} className="rounded-md border border-slate-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-display font-bold text-lg truncate">{u.name}</div>
                <div className="text-xs font-mono text-slate-500 mt-0.5">@{u.username}</div>
              </div>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                {ROLE_LABEL[u.role] || u.role}
              </span>
            </div>
            {u.role === "ppdp" && u.assigned_tps && (
              <div className="mt-3 text-xs">
                <div className="text-slate-500 uppercase tracking-[0.15em] font-semibold mb-1">Area Tugas (TPS)</div>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-0.5 text-xs font-semibold">
                  <MapPin className="h-3 w-3" /> {u.assigned_tps}
                </span>
              </div>
            )}
            {u.role !== "ppdp" && (u.assigned_areas?.length > 0 || u.assigned_dusuns?.length > 0) && (
              <div className="mt-3 text-xs">
                <div className="text-slate-500 uppercase tracking-[0.15em] font-semibold mb-1">Area Tugas</div>
                <AssignedAreasBadges
                  areas={
                    u.assigned_areas?.length
                      ? u.assigned_areas
                      : (u.assigned_dusuns || []).map((d) => ({ dusun: d }))
                  }
                />
              </div>
            )}
            {u.role === "ppdp" && !u.assigned_tps && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Belum ada TPS yang di-assign. Klik <b>Ubah</b> untuk memilih TPS.
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditing(u); setOpenForm(true); }} className="flex-1" data-testid={`edit-user-${u.id}`}>
                <Pencil className="h-4 w-4 mr-1" /> Ubah
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleDelete(u)} className="text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full rounded-md border border-slate-200 bg-white p-10 text-center text-slate-500">
            <Users2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
            Belum ada pengguna.
          </div>
        )}
      </div>

      <UserFormDialog
        open={openForm}
        onClose={() => { setOpenForm(false); setEditing(null); }}
        onSave={handleSave}
        user={editing}
        dusuns={dusuns}
        tpsList={tpsList}
      />
    </div>
  );
}

function UserFormDialog({ open, onClose, onSave, user, dusuns, tpsList }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(user ? {
      ...user,
      password: "",
      assigned_tps: user.assigned_tps || "",
      assigned_areas: user.assigned_areas?.length
        ? user.assigned_areas
        : (user.assigned_dusuns || []).map((d) => ({ dusun: d, rw: "", rt: "" })),
    } : {
      username: "", password: "", name: "", role: "ppdp", assigned_tps: "", assigned_areas: [], assigned_dusuns: [],
    });
  }, [user, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedTps = form.role === "ppdp" && form.assigned_tps
    ? (tpsList || []).find((t) => t.tps_kode === form.assigned_tps)
    : null;

  const submit = (e) => {
    e.preventDefault();
    if (!user && (!form.username || !form.password || !form.name)) {
      toast.error("Username, password, dan nama wajib diisi"); return;
    }
    if (form.role === "ppdp" && !form.assigned_tps) {
      toast.error("Pilih TPS untuk area tugas PPDP"); return;
    }
    const payload = { ...form };
    if (user && !payload.password) delete payload.password;
    if (form.role === "ppdp") {
      // Untuk PPDP, area tugas cukup dari TPS. Kosongkan legacy fields.
      payload.assigned_tps = form.assigned_tps;
      payload.assigned_areas = [];
      payload.assigned_dusuns = [];
    } else {
      payload.assigned_tps = null;
      // Sanitize areas: drop empty dusun rows, normalize empty strings -> null
      payload.assigned_areas = (payload.assigned_areas || [])
        .filter((a) => a && a.dusun)
        .map((a) => ({ dusun: a.dusun, rw: a.rw || null, rt: a.rt || null }));
      // Keep legacy list in sync for clients still reading old field
      payload.assigned_dusuns = Array.from(new Set(payload.assigned_areas.map((a) => a.dusun)));
    }
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{user ? "Ubah Pengguna" : "Tambah Pengguna"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="user-form">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Nama Lengkap *</Label>
            <Input value={form.name || ""} onChange={(e) => update("name", e.target.value)} className="h-11" data-testid="form-user-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Username *</Label>
              <Input value={form.username || ""} onChange={(e) => update("username", e.target.value.toLowerCase())} disabled={!!user} className="h-11 font-mono" data-testid="form-user-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Password {user && <span className="text-slate-500 font-normal">(kosongkan jika tidak diubah)</span>}</Label>
              <Input type="password" value={form.password || ""} onChange={(e) => update("password", e.target.value)} className="h-11" data-testid="form-user-password" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Role *</Label>
            <Select value={form.role} onValueChange={(v) => update("role", v)}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">NIK</Label>
              <Input value={form.nik || ""} onChange={(e) => update("nik", e.target.value)} className="h-11 font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">No. HP</Label>
              <Input value={form.no_hp || ""} onChange={(e) => update("no_hp", e.target.value)} className="h-11 font-mono" />
            </div>
          </div>
          {form.role === "ppdp" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Area Tugas — Pilih TPS *</Label>
              <select
                value={form.assigned_tps || ""}
                onChange={(e) => update("assigned_tps", e.target.value)}
                className="h-11 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
                data-testid="form-user-tps"
              >
                <option value="">— Pilih TPS —</option>
                {tpsList.map((t) => (
                  <option
                    key={t.tps_kode}
                    value={t.tps_kode}
                    disabled={t.ppdp_count > 0 && !(user && user.assigned_tps === t.tps_kode)}
                  >
                    {t.tps_kode} · {t.dusun} · {t.total_pemilih.toLocaleString("id-ID")} pemilih
                    {t.ppdp_count > 0 ? " · SUDAH DI-ASSIGN" : ""}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500">1 PPDP hanya melayani 1 TPS. Wilayah RW/RT otomatis dari data pemilih.</p>

              {selectedTps && (
                <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-3" data-testid="tps-info-panel">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-2">Wilayah Kerja (Read-only)</div>
                  <div className="grid grid-cols-2 gap-3 text-sm mb-2">
                    <div>
                      <div className="text-[11px] text-slate-500">Dusun</div>
                      <div className="font-semibold">{selectedTps.dusun || "-"}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-500">Total Pemilih</div>
                      <div className="font-semibold tabular-nums">
                        {selectedTps.total_pemilih.toLocaleString("id-ID")}
                        <span className="ml-2 text-[10px] font-normal">
                          <span className="rounded bg-blue-100 text-blue-700 px-1">L {(selectedTps.laki_laki || 0).toLocaleString("id-ID")}</span>{" "}
                          <span className="rounded bg-pink-100 text-pink-700 px-1">P {(selectedTps.perempuan || 0).toLocaleString("id-ID")}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {selectedTps.rw_breakdown.map((rw) => (
                      <div key={rw.rw} className="text-xs bg-white rounded border border-slate-200 px-2 py-1.5">
                        <span className="font-semibold">RW {rw.rw}</span>
                        <span className="text-slate-400"> · </span>
                        <span className="text-slate-600">RT {rw.rts.map((rt) => `${rt.rt} (${rt.jumlah})`).join(", ")}</span>
                        <span className="text-slate-400 float-right tabular-nums">Σ {rw.subtotal}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {(form.role === "rt" || form.role === "rw") && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                Area Tugas (Dusun → RW{form.role === "rt" && " → RT"})
              </Label>
              <AssignedAreasEditor
                value={form.assigned_areas || []}
                onChange={(areas) => update("assigned_areas", areas)}
                dusuns={dusuns}
              />
              {form.role === "rt" && (
                <p className="text-[11px] text-amber-700">Untuk Ketua RT: lengkapi Dusun, RW, dan RT.</p>
              )}
              {form.role === "rw" && (
                <p className="text-[11px] text-amber-700">Untuk Ketua RW: lengkapi Dusun dan RW (RT dikosongkan).</p>
              )}
            </div>
          )}
          {user && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="active" checked={form.active !== false} onChange={(e) => update("active", e.target.checked)} />
              <Label htmlFor="active" className="text-sm">Akun aktif</Label>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="user-form-submit">
              {user ? "Simpan" : "Tambah"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
